import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import apiService from '../../services/api';

const ProgramSports = ({ programId }) => { // Accept programId as prop
  const { t } = useAppContext();
  const [sportsInfo, setSportsInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch sports info (wrapped in useCallback)
  const fetchSportsInfo = useCallback(async () => {
    if (!programId) return; // Use programId prop

    setIsLoading(true);
    setError(null);

    try {
      const data = await apiService.getSportsInfo(programId); // Use programId prop
      setSportsInfo(data);
    } catch (err) {
      console.error('Failed to fetch sports information:', err);
      setError(t('programSports.error'));
      setSportsInfo(null); // Set to null on error
    } finally {
      setIsLoading(false);
    }
  }, [programId]); // Dependency on programId

  // Fetch data on mount or when programId changes
  useEffect(() => {
    fetchSportsInfo();
  }, [fetchSportsInfo]);

  // Format date to a more readable format
  // Note: Date-only strings like "2012-06-20" are parsed as UTC midnight,
  // which can shift to the previous day when converted to local time.
  // We use UTC methods to avoid this timezone issue.
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  // Format time to a more readable format
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      // Parse the time string (e.g., "15:30:00-04:00")
      const timeParts = timeString.split('-');
      const time = timeParts[0];
      const [hours, minutes] = time.split(':');

      // Convert to 12-hour format
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;

      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return timeString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-500">
        <p>{error}</p>
        <button
          className="mt-2 px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
          onClick={fetchSportsInfo} // Retry fetch
        >
          {t('programSports.tryAgain')}
        </button>
      </div>
    );
  }

  if (!sportsInfo) {
    return (
      <div className="p-6 text-center text-gray-500">
        <p>{t('programSports.noInfo')}</p>
      </div>
    );
  }

  // Separate teams into home and away
  const homeTeam = sportsInfo.teams?.find(team => team.isHome === 1);
  const awayTeam = sportsInfo.teams?.find(team => team.isHome !== 1); // Catches 0 and null

  return (
    <div className="p-6 overflow-auto h-full" style={{ maxHeight: 'calc(100vh - 300px)' }}>
      <h2 className="text-xl font-semibold mb-6 text-left">{t('programSports.title')}</h2>

      {/* Teams Section */}
      {(homeTeam || awayTeam) && (
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4 text-left">{t('programSports.teams')}</h3>
          <div className="flex flex-col md:flex-row justify-around items-center gap-8 p-4 bg-gray-50 border border-gray-200 rounded-md">
            {/* Away Team */}
            <div className="flex flex-col items-center text-center">
              {awayTeam?.logo_url ? (
                <img
                  src={awayTeam.logo_url}
                  alt={`${awayTeam.team || t('programSports.awayTeamNA')} logo`}
                  className="h-24 w-24 object-contain mb-2"
                  onError={(e) => { e.target.style.display = 'none'; /* Hide if logo fails */ }}
                />
              ) : (
                <div className="h-24 w-24 flex items-center justify-center bg-gray-200 rounded mb-2 text-gray-500">
                  {t('programSports.noLogo')}
                </div>
              )}
              <p className="font-semibold">{awayTeam?.team || t('programSports.awayTeamNA')}</p>
              <p className="text-sm text-gray-600">{t('programSports.away')}</p>
            </div>

            <div className="text-2xl font-bold text-gray-400">VS</div>

            {/* Home Team */}
            <div className="flex flex-col items-center text-center">
              {homeTeam?.logo_url ? (
                <img
                  src={homeTeam.logo_url}
                  alt={`${homeTeam.team || t('programSports.homeTeamNA')} logo`}
                  className="h-24 w-24 object-contain mb-2"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="h-24 w-24 flex items-center justify-center bg-gray-200 rounded mb-2 text-gray-500">
                  {t('programSports.noLogo')}
                </div>
              )}
              <p className="font-semibold">{homeTeam?.team || t('programSports.homeTeamNA')}</p>
              <p className="text-sm text-gray-600">{t('programSports.home')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Game Details Section - Formatted as requested */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-left">{t('programSports.gameDetails')}</h3>
        <div className="bg-white border border-gray-200 rounded-md p-4 text-sm">
          {/* Line 1: Date, Time, Timezone, Venue */}
          <p className="mb-1">
            {formatDate(sportsInfo.gameDate)} {formatTime(sportsInfo.gameTime)}
            {sportsInfo.gameTimeZone && ` (${sportsInfo.gameTimeZone.replace(' Observing', '')})`}
            {sportsInfo.venue && ` @ ${sportsInfo.venue}`}
          </p>

          {/* Line 2: Season, Season Type */}
          <p className="mb-1 text-gray-700">
            {t('programSports.season')} {sportsInfo.season || 'N/A'}
            {sportsInfo.season_type && ` (${sportsInfo.season_type})`}
          </p>

          {/* Line 3: Playoffs (Optional) */}
          {sportsInfo.playoffRound && (
            <p className="text-gray-700">
              {t('programSports.playoffs')} {sportsInfo.playoffRound}
              {sportsInfo.playoffRound_gameNum ? ` ${t('programSports.gameNum', { gameNum: sportsInfo.playoffRound_gameNum })}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Optional: Raw Teams Data Table (if needed for debugging/completeness) */}
      {/*
      {sportsInfo.teams && sportsInfo.teams.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2 text-left">Raw Team Data</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Team</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Team Brand ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Home Team</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Logo URL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sportsInfo.teams.map((team, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-sm text-gray-900">{team.team || 'N/A'}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{team.teamBrandId || 'N/A'}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">
                      {team.isHome === null ? 'Unknown' : team.isHome ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 break-all">{team.logo_url || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      */}

      <div className="text-xs text-gray-400 mt-6 text-right">
        {t('programSports.dataFetched')} {new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
      </div>
    </div>
  );
};

export default ProgramSports;
